import { Box, Button, Checkbox, Drawer, IconButton, MenuItem, Radio, Select, Typography } from '@mui/material'
import { useFilters } from '../FiltersContext'
import { CloseOutlined } from '@mui/icons-material'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo } from 'react'
import { format } from 'date-fns'
import { DatePicker } from '@mui/x-date-pickers'
import { renderFormattedRange } from '../utils/render-formatted-range'
import { getFinishRange, getStartRange } from '../utils/get-data-range-range'
import { FiltersContextValue } from '../FiltersContext.types'

export enum DateRange {
  Current = 'current',
  Today = 'today',
  CurrentMonth = 'currentMonth',
  Last7Days = 'last7Days',
  LastMonth = 'lastMonth',
  All = 'all',
  Custom = 'custom',
}

export type FormFields = {
  dateRange: DateRange
  columns: string[]
  from: Date
  to: Date
}

const rangeTypes = [...Object.values(DateRange)]

type ExportFormProps = {
  columnsToExport?: FiltersContextValue['columnsToExport']
}

export const ExportForm = ({ columnsToExport }: ExportFormProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'export' })
  const { changeExportMode, filters, isExportMode, onExport } = useFilters()

  const userFilters = useMemo(() => {
    return Object.entries(filters).filter(
      ([key, value]) =>
        !(
          ['order', 'orderField', 'take', 'page', 'tab'].includes(key) ||
          (key === 'status' && value instanceof Array && value.length === 0)
        )
    )
  }, [filters])

  const { handleSubmit, watch, setValue, control } = useForm<FormFields>({
    defaultValues: {
      dateRange: DateRange.All,
      from: new Date(),
      to: new Date(),
      columns: (columnsToExport || []).filter(el => el.disabled).map(el => el.field),
    },
    resolver: yupResolver(yup.object()),
  })

  const currentRangeExist = useMemo(
    () =>
      userFilters.some(([fieldName]) => fieldName === 'createdAtLocalStart' || fieldName === 'createdAtLocalFinish'),
    [userFilters]
  )

  useEffect(() => {
    if (watch('dateRange') === DateRange.Current && !currentRangeExist) {
      setValue('dateRange', DateRange.All)
    }
  }, [currentRangeExist])

  const getStartRangeDate = useCallback((data: FormFields) => getStartRange(data, filters), [filters])
  const getFinishRangeDate = useCallback((data: FormFields) => getFinishRange(data, filters), [filters])

  const onAcceptExportSettings = (data: FormFields) => {
    const rangeStart = getStartRangeDate(data)
    const rangeFinish = getFinishRangeDate(data)

    const marketExist = 'marketId' in filters

    const columns = columnsToExport
      ?.filter(el => data.columns.includes(el.field))
      .map(el => `${el.field}:${el.label}`)
      .join(',')

    onExport?.({
      ...filters,
      columns: columns || '',
      [marketExist ? 'createdAtLocalStart' : 'createdAtStart']: rangeStart
        ? format(new Date(rangeStart), 'yyyy-MM-dd 00:00:00')
        : '',
      [marketExist ? 'createdAtLocalFinish' : 'createdAtFinish']: rangeFinish
        ? format(new Date(rangeFinish), 'yyyy-MM-dd 23:59:59')
        : '',
    })
  }

  const isSomeColumnsSelected = (columnsToExport || []).some(
    ({ field, disabled }) => watch('columns')?.includes(field) && !disabled
  )

  const sortedColumns = useMemo(
    () => (columnsToExport ?? []).sort((a, b) => b.label.localeCompare(a.label)).sort((a, b) => (b.disabled ? 1 : -1)),
    [columnsToExport]
  )

  return (
    <Drawer
      anchor="right"
      open={isExportMode}
      PaperProps={{
        sx: {
          bottom: 14,
          top: 14,
          right: 14,
          height: 'auto',
          minWidth: 640,
          borderRadius: 2,
          backgroundColor: 'grey.200',
          backgroundImage: 'none',
        },
      }}
      onClose={() => {
        changeExportMode?.(false)
      }}
    >
      <Box display="flex" justifyContent="space-between" p={2}>
        <Box>
          <Typography component="div" variant="h5" fontWeight={600}>
            {t('title')}
          </Typography>
          <Typography component="div" variant="body2" color="grey.600">
            {t('description')}
          </Typography>
        </Box>
        <IconButton
          onClick={() => {
            changeExportMode?.(false)
          }}
        >
          <CloseOutlined />
        </IconButton>
      </Box>
      <Box
        m={2}
        mt={0}
        p={2}
        height="100%"
        sx={{ backgroundColor: 'grey.50', border: 'border: 1px solid #EAEDF2', borderRadius: 2 }}
      >
        <form
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          onSubmit={handleSubmit(onAcceptExportSettings)}
        >
          <Typography component="div" variant="h6" fontWeight={500}>
            {t('range')}
          </Typography>
          <Controller
            control={control}
            name="dateRange"
            render={({ field }) => (
              <Box>
                {rangeTypes
                  .filter(item => item !== 'current' || (item === 'current' && currentRangeExist))
                  .map(item => (
                    <Box key={`option-${item}`} display="flex" justifyContent="space-between">
                      <Typography component="label" variant="body1">
                        <Radio {...field} value={item} checked={field.value === item} /> {t(item)}
                      </Typography>
                      <Box color="grey.600" flexBasis={300}>
                        {(() => {
                          if (item === DateRange.All) {
                            return '-'
                          }
                          if (item === DateRange.Current) {
                            const startDate = filters?.createdAtLocalStart
                              ? (filters.createdAtLocalStart as string)
                              : ''

                            const finishDate = filters.createdAtLocalFinish
                              ? (filters.createdAtLocalFinish as string)
                              : ''

                            const formattedStart = startDate ? format(new Date(startDate), 'MMM dd, yyyy') : 'n/a'
                            const formattedFinish = finishDate ? format(new Date(finishDate), 'MMM dd, yyyy') : 'n/a'

                            return `${formattedStart} - ${formattedFinish}`
                          }

                          if (item === DateRange.Custom) {
                            return (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Controller
                                  name="from"
                                  control={control}
                                  render={({ field: fromField }) => (
                                    <Box>
                                      {field.value === item ? (
                                        <DatePicker sx={{ width: 170 }} {...fromField} />
                                      ) : (
                                        format(watch('from'), 'MMM dd, yyyy')
                                      )}
                                    </Box>
                                  )}
                                />
                                {' - '}
                                <Controller
                                  name="to"
                                  control={control}
                                  render={({ field: toField }) => (
                                    <Box>
                                      {field.value === item ? (
                                        <DatePicker sx={{ width: 170 }} {...toField} />
                                      ) : (
                                        format(watch('to'), 'MMM dd, yyyy')
                                      )}
                                    </Box>
                                  )}
                                />
                              </Box>
                            )
                          }

                          return renderFormattedRange(item)
                        })()}
                      </Box>
                    </Box>
                  ))}
              </Box>
            )}
          />

          {columnsToExport?.length && (
            <Box mt={4}>
              <Typography component="div" variant="body1" mb={1} fontWeight={500}>
                {t('columnsToExport')}
              </Typography>
              <Box display="flex" width="100%" gap={1}>
                <Controller
                  control={control}
                  name="columns"
                  render={({ field }) => (
                    <Select
                      {...field}
                      fullWidth
                      multiple
                      renderValue={value => <Typography color="#888E9E">Selected ({value.length})</Typography>}
                    >
                      {sortedColumns.map(item => (
                        <MenuItem
                          disabled={item.disabled}
                          key={item.field}
                          value={item.field}
                          sx={{ textTransform: 'capitalize' }}
                        >
                          <Checkbox checked={watch('columns')?.includes(item.field)} sx={{ p: 0.5 }} />
                          {item.label.toLowerCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <Button
                  sx={{ width: 120 }}
                  onClick={() => {
                    setValue(
                      'columns',
                      isSomeColumnsSelected
                        ? columnsToExport.filter(item => item.disabled).map(el => el.field)
                        : columnsToExport.map(el => el.field)
                    )
                  }}
                >
                  {isSomeColumnsSelected ? 'Unselect all' : 'All'}
                </Button>
              </Box>
              <Typography color="#888E9E" variant="body2" mt={3} sx={{ maxWidth: 560 }}>
                {columnsToExport
                  .filter(el => watch('columns').includes(el.field))
                  .map((item, idx, arr) => (
                    <span key={`selected-${item.field}`}>
                      {item.label}
                      {idx === arr.length - 1 ? '' : ', '}
                    </span>
                  ))}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', mt: 'auto', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={() => {
                  changeExportMode?.(false)
                }}
              >
                {t('cancel')}
              </Button>
              <Button fullWidth variant="contained" color="primary" type="submit">
                {t('accept')}
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Drawer>
  )
}
